import {classNames, InfiniteScroll, React, useLocalStore, observer, _, useAsObservableSource, useEffect, jsonComputedOptions, Keys, utils, useCallback, useMemo, immer, IDisposer} from '@api'
import {Icon, InputGroup, Popover, PopoverPosition, Spinner, Classes} from '@blueprintjs/core'
import {Popover2, Tooltip2} from '@api'
import {Card, CardActions, CardContent, Divider, Tooltip} from '@material-ui/core'
import {Button} from '@blueprintjs/core'
import {CardFieldStoreContext, DEBUG_CATALOG_ITEM_CARDS, IContextMenuItems, useVipLocalStore, VipLocalStore} from '@modules'
import {LibraryFiltersPopoverButton} from '@modules/catalog'
import {FieldRendererStore, useFieldRendererStore} from '@design-board/stores'
import {NestedXRefCards} from '@modules/CatalogItemDetails'
import {LoadingIndicator} from '@modules/common'
import {ICatalogFieldConfig, deployments, nav, WorkspaceConfig, ui, ISectionConfig, onSiteError, StoresInstance, ImageRecord} from '@stores'
import {removeUndefinedsNested} from '@utils'
import {ListPreviewCardRefCatalogItem, ListPreviewCardRefListCatalogItems, MyImage, Placeholder, PrimaryIconButton} from 'centric-ui-components'
import {action, autorun, computed, observable, reaction, runInAction, toJS, when} from 'mobx'
import {Fragment, useContext} from 'react'
import * as css from './SidebarSpecification.css'
import {SidebarSpecificationCardContextMenu} from './SidebarSpecificationCardContextMenu'

class SidebarSpecificationStore extends VipLocalStore {
  availableSpecs = observable.array([], {deep: false})
  @observable total: number = 0
  @observable isSearch: boolean = false
  @observable search: string = ''

  @observable mouseOver: boolean = false

  private DATA_LOAD_SIZE: number = 50

  constructor(stores: StoresInstance, public props: { field: ICatalogFieldConfig, info }, public rendererStore: FieldRendererStore) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {queryPayload, everOpened, runQuery} = this

        if (everOpened) {
          runQuery(0)
        }
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }
  //  return reaction(
  //       () => store.queryPayload,
  //       () => {
  //         everOpened && store.runQuery(0)
  //       })
  //

  @computed get field() { return this.props.field}

  @computed get info() { return this.props.info}

  @computed get index() {
    return this.field?.esIndex
  }

  @computed get readonly() {
    return !this.rendererStore.editable
  }

  @computed get hasMore() {
    return !this.loading && (!this.availableSpecs || this.availableSpecs.length < this.total)
  }

  @computed get header() {
    const {field, catalog} = this
    return catalog.labelForField(field, '', 'singular')
  }

  @computed get associatedLibrary() {
    return this.catalog.libraries.find(l => l.id == this.field?.workspace ?? l.index == this.index)
  }

  @computed get libraryFilter() {
    const {stores: {filter: {storeByLibraryId}}, associatedLibrary} = this
    return associatedLibrary ? storeByLibraryId.get(associatedLibrary.name) : null
  }

  @computed get dataMap() {
    return this.associatedLibrary?.dataMap ?? this.associatedWorkspace?.dataMap
  }

  @computed get associatedWorkspace(): WorkspaceConfig {
    return this.catalog?.workspaceByIndex[this.index]
  }

  @computed get associatedCatalogField(): ICatalogFieldConfig {
    const {associatedWorkspace} = this
    return associatedWorkspace?.catalog.fields.find(f => f?.card?.default && f?.field !== 'attributes.tags')
  }

  @computed get associatedConstraintTerm() {
    const {field, rendererStore: {data}} = this,
      // YFTS-7083
      // constrainedAssignedValue = field.constraint?.from && (field.constraint?.from !== `${field.field}.${field.constraint?.to}`) ? _.get(data, field?.constraint?.from) : null
      constrainedAssignedValue = (field?.constraint?.filterOnSidebar !== false && field.constraint?.from) ? _.get(data, field?.constraint?.from) : null

    return constrainedAssignedValue ? {
      term: {[field.constraint.to]: constrainedAssignedValue}
    } : undefined
  }

  @computed(jsonComputedOptions) get queryPayload() {
    const {es, catalog, search, field, cardFields, associatedWorkspace, associatedLibrary, libraryFilter, associatedConstraintTerm} = this

    return removeUndefinedsNested({
      bool: {
        must: search !== '' ? {
          query_string: {
            fields: cardFields.map(({field}) => `${field}.text`),
            query: `*${es.tokenizeQueryString(search)}*`,
            default_operator: 'AND'
          }
        } : undefined,
        filter: [
          associatedConstraintTerm,
          ...catalog.es.seasonSelectorRestrictedTermsFor(associatedWorkspace),
          //specListFilter,
          // es.filterForNonDeletedItems,
          ...(libraryFilter?.filterTerms ?? []),
          ...catalog.whereForLibrary(associatedLibrary)
        ].filter(_.identity)
      }
    })
  }

  @observable loading = false
  @observable initialLoad = true
  @observable page = 0

  runQuery = _.debounce((page: number) => this._runQuery(page), 200)

  @action _runQuery = async (page: number) => {
    // console.log(`runQuery(${page})`)
    if (this.loading) {
      when(() => !this.loading, () => this._runQuery(page))
      return
    }

    this.loading = true
    this.page = page
    try {
      const {availableSpecs, es, index, DATA_LOAD_SIZE, queryPayload} = this,
        {hits: {hits}, aggregations: {count: {value: totalValue}}} = await es.search({
          index,
          meta: `SidebarSpecification.runQuery(${page}) - ${index}`,
          body: {
            query: queryPayload,
            from: DATA_LOAD_SIZE * page,
            size: DATA_LOAD_SIZE,
            sort: [
              {name: {order: 'asc', unmapped_type: 'string'}},
              {createAt: {order: 'asc', unmapped_type: 'date'}}
            ],
            aggs: {
              count: {
                value_count: {field: '_id'}
              }
            }
          }
        }, {log: {query: false}})

      this.total = totalValue

      //console.log({page, index, queryPayload: toJS(queryPayload)})

      //console.log({whereValue, data})
      //Concurrent calls making data corrupt if extracted from "this" before await
      runInAction(() => {
        page === 0 ? availableSpecs.replace(hits) : availableSpecs.push(...hits)
      })
    } catch (err) {
      onSiteError(err)
    } finally {
      this.loading = false
      this.initialLoad = false
    }
  }

  @action onChangeSpec = (d) => {
    const {field} = this
    // if (field.onAssign) {
    //   const mutations = {}, {} = this
    //   const {rendererStore: {detailsStore, cardFieldStore}} = this
    //
    //   field.onAssign({field, mutations, libraryItem: d, stores: null, data: detailsStore?.data ?? cardFieldStore?.data})
    // }
    //
    this.rendererStore.onChangeSpec(d, field.field)
    //this.detailsStore.mutateLocalField({fieldObj: this.field, newValue: d})
  }

  @computed get isList() {
    return this.field?.def?.type === 'RefList'
  }

  @action onSelectSpecItemFromList = (d) => {
    this.rendererStore.onChangeSpec(d, this.rendererStore.field.assignToXrefField)
  }

  @action onClickNestedCards = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }

  @observable.ref inputRef: HTMLInputElement
  saveInputRef = (r: HTMLInputElement) => {
    this.inputRef = r
  }

  @observable.ref inputGroupRef: InputGroup
  saveInputGroupRef = (r: InputGroup) => {
    this.inputGroupRef = r
  }

  @action toggleSearch = () => {
    this.isSearch = !this.isSearch
    this.search = ''

    if (this.isSearch) {
      setTimeout(() => this.inputRef?.focus(), 100)
    }
  }

  @action _changeSearch = (e) => {
    this.search = e.target.value
  }

  @observable everOpened = false
  @action popoverOpening = () => {
    const {catalog} = this
    catalog.currentClickPopoverBuilderCardField = null
    this.everOpened = true
    const {rendererStore: {cardFieldStore}} = this
    cardFieldStore && (cardFieldStore.specPopoverOpen = true)
    catalog.currentClickPopoverBuilderCardField = cardFieldStore
    this.isPopoverOpen = true
  }

  @observable isPopoverOpen = false

  @action reset = () => {
    this.isSearch = false
    this.isPopoverOpen = false
    this.search = ''
    if (this.libraryFilter) {
      this.libraryFilter.showLibraryFilterPopover = null
    }

    const {catalog, rendererStore: {cardFieldStore}} = this
    cardFieldStore && (cardFieldStore.specPopoverOpen = false)

    catalog.onClickPopoverClosing(cardFieldStore)
  }

  @action onDelete = (e) => {
    e.stopPropagation()
    e.preventDefault()
    this.rendererStore.deleteSpec(this.field.field)
  }

  @computed get cardFields(): Array<SidebarSpecFieldValue> {
    const {catalog, dataMap, associatedLibrary, associatedWorkspace} = this

    if (false && associatedLibrary?.cardUI) {
      //const {cardUI: {main, extra, secondary}} = associatedLibrary
      //return _.chain([...(main)])
    } else {
      const sections = dataMap?.sections ?? [{fields: [{field: dataMap?.title ?? 'name', style: 'h1'}, {field: dataMap?.subtitle1, style: 'h2'}, {field: dataMap?.subtitle2, style: 'h2'}, {field: dataMap?.subtitle3, style: 'h2'}, {field: dataMap?.active, style: 'h3'}].filter(f => f.field)}]

      const r = _.flatMap(sections, ({fields}) => (fields ?? []).map(s => ({
        ...s,
        fieldConfig: catalog.fieldForWorkspace(s.field, associatedWorkspace),
        label: catalog.labelFor_workspace(s.field, associatedWorkspace, '')
      } as SidebarSpecFieldValue)))
      //console.log({associatedWorkspace, sections, r})

      return r
    }
  }
}

export interface SidebarSpecFieldValue extends ISectionConfig {
  style?: 'h1' | 'h2'
  fieldConfig: ICatalogFieldConfig
  label?: string
  value?: any
}

export class SpecificationCardStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance, public props: { selected?: boolean, data: any, specStore: SidebarSpecificationStore }) {
    super(stores)

    this.sanitizeData()
  }

  @computed get id() {
    const {catalog: {idFor}, data} = this
    return idFor(data, false)
  }

  @action onDoubleClickCard = (e) => {
    const {data, id, catalog, associatedWorkspace, props: {specStore: {rendererStore: {catalogField, detailsStore}}}, flexLayout: {activeTab}} = this
    // console.log(`onDoubleClick`, {id, selectedPathIndex, path})

    activeTab && activeTab.openCatalogItemDetailsInNewTab({
      path: [
        {
          esIndex: associatedWorkspace?.workspace?.catalog?.index ?? catalog.index,
          where: [{value: id}],
          catalogField: catalogField?.field
        }
      ],
      workspace: associatedWorkspace?.workspace?._id,
      selectedPathIndex: 0
    })
  }

  /**
   * Assign an item from a library to a spec field
   * @param e
   */
  @action onClickCard = (e) => {
    const {props: {selected}} = this

    if (!selected) {
      const {data, props: {specStore: {info, field, onChangeSpec, isList, rendererStore, rendererStore: {catalogField, cardFieldStore, detailsStore: {data: detailsStoreData}, originalData}}}} = this,
        clickOrigin = e.currentTarget
      let updatedData = data
      if (e.which === Keys.TAB || e.which === Keys.ENTER) {
        let element = document.getElementById(catalogField.esIndex)
        element?.focus()
      }
      if (field?.onAssign && clickOrigin.getAttribute('data-in-popover') == 'true') { // To avoid mutations on click of specification card but to go ahead with popover records
        let mutations = {}
        field?.onAssign({field, mutations, libraryItem: updatedData, stores: this.stores, data: detailsStoreData ?? cardFieldStore?.data, candidateOnly: false})
        !_.isEmpty(mutations) && _.keys(mutations).forEach(mutatedKey => {
          const mutatedData = mutations[mutatedKey]?.updatedValue
          rendererStore.onChangeSpec(mutatedData, mutatedKey)
        })
      }
      const fieldValue = info ?? _.get(originalData, field?.field)
      if (isList) {
        const existing = _.find(fieldValue || [], d => d?.exId == data?.exId)
        if (!existing) {
          // add it
          onChangeSpec([...(fieldValue || []), data].filter(_.identity))
        } 
      } else if (fieldValue?.id !== data?.id) {
        onChangeSpec(data)
      }
    } else {
      this.removeSpecItem()
    }
  }

  @action removeSpecItem = () => {
    const {data, props: {specStore: {info, field, onChangeSpec, isList}}} = this
    if (isList) {
      const updatedData = [...info]
      _.remove(updatedData, (d: any) => d.exId === data.exId)
      onChangeSpec(updatedData)
    } else {
      onChangeSpec(null)
    }
  }

  @observable.ref data: any

  @action sanitizeData = () => {
    this.data = this.props.data
    // this.data = produce(this.props.data, draft => {
    //
    //   (this.props?.specStore?.associatedWorkspace?.catalog?.fields ?? []).forEach(f => {
    //     if (f.defaultValue && !_.has(draft, f.field)) {
    //       _.set(draft, f.field, f.defaultValue)
    //     }
    //   })
    // })
  }

  onContextMenu = e => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  get contextMenuItems() {
    return (
      <CardFieldStoreContext.Provider value={this.props.specStore.rendererStore.cardFieldStore}>
        <SpecificationCardStoreContext.Provider value={this}>
          <SidebarSpecificationCardContextMenu/>
        </SpecificationCardStoreContext.Provider>
      </CardFieldStoreContext.Provider>
    )
  }

  @computed get fieldValues(): Array<SidebarSpecFieldValue> {
    const {strings, props: {specStore: {cardFields}}, data, renderSpecAsText} = this

    const values = cardFields.map(s => ({
      ...s,
      value: _.get(data, s.field) ?? strings.emptyLabel
    }))
    return renderSpecAsText ? values.filter(f => f.value !== '--') : values
  }

  @computed get title() {
    const {props: {specStore: {dataMap, associatedCatalogField}}, data} = this
    return (dataMap ? _.get(data, dataMap.title) : _.map(associatedCatalogField?.card?.titleFields, title => _.get(data, title)).join(', ')) || '--'
  }

  @computed get subtitle() {
    const {props: {specStore: {dataMap, associatedCatalogField}}, data} = this
    return (dataMap ? _.get(data, dataMap.subtitle1) : _.get(data, associatedCatalogField?.field)) || '--'
  }

  @computed get firstImage(): ImageRecord {
    const {props: {specStore: {dataMap}}, data} = this
    const images = _.get(data, dataMap?.images ?? 'images')
    return _.isArray(images) ? _.first(images) : images
  }

  @computed get image() {
    const {firstImage, catalog} = this
    return catalog.imageUrlFor(firstImage)
  }

  @action promoteSpecItem = () => {
    const {data, props: {specStore: {info, onSelectSpecItemFromList}}} = this
    onSelectSpecItemFromList(data)
  }

  @computed get disableListItem() {
    const {data, props: {specStore: {rendererStore: {field: {assignToXrefField}, detailsStore: {data: catalogItemData}}}}} = this
    return assignToXrefField && catalogItemData[assignToXrefField]?.exId === data?.exId
    // return true
  }

  @computed get associatedWorkspace() {
    const {catalog, props: {specStore: {field: {esIndex}}}} = this
    return esIndex ? catalog.fieldByWorkspaceIndex[esIndex] : null
  }

  @action ignoreHyperlinkClick = (e: React.MouseEvent) => {
    e.preventDefault()
  }

  @computed get specUrl() {
    const {data, associatedWorkspace, catalog, stores: {sidebar}} = this
    return !data || !associatedWorkspace || associatedWorkspace.workspace.visible === false
           ? null
           : nav.urlFor_string({workspace: associatedWorkspace.workspace.config, queryParams: {deployment: deployments.selectedDeploymentId, ...sidebar.queryParamsFor({right: {open: true, path: [{where: [{value: catalog.idFor(data)}]}]}})}})
  }

  @computed get renderSpecAsText() {
    const {props: {specStore: {field}}} = this
    return field?.renderer == 'text'
  }
}

export const SpecificationCardStoreContext = React.createContext<SpecificationCardStore>(undefined)
export const useSpecificationCardStore = () => useContext(SpecificationCardStoreContext)

export const SidebarSpecificationStoreContext = React.createContext<SidebarSpecificationStore>(undefined)
export const useSidebarSpecificationStore = () => useContext(SidebarSpecificationStoreContext)

/**
 * A card representing a single linked library item for this catalog
 * For example an article, a color, a material, or a shape.
 * May be rendered as a 'big field' with image/title/subtitle/badges/etc or might be one of many 'chips' linked to an item (mpv -> articles, mpv -> colors, etc)
 * @see {SpecificationItem}
 */
export const SidebarSpecificationCard = observer<{ data?: any, popover?: boolean, allowUnassign?: boolean, selected?: boolean }>(function SidebarSpecificationCard({allowUnassign, data, popover, selected = false}) {
  const specStore = useSidebarSpecificationStore(),
    obsProps = useAsObservableSource({data, specStore, selected}),
    store = useVipLocalStore((ws) => new SpecificationCardStore(ws, obsProps)),
    {
      strings, workspaces, catalog, onClickCard, onDoubleClickCard, specUrl, renderSpecAsText, ignoreHyperlinkClick, image, fieldValues, promoteSpecItem, removeSpecItem, disableListItem, onContextMenu, firstImage,
      props: {specStore: {readonly, rendererStore: {field: detailsField, cardFieldStore, catalogField}, isList, mouseOver, onDelete, field, dataMap, associatedLibrary}}
    } = store,
    isArrayField = _.includes(['RefList', 'TagList', 'EnumList'], catalogField?.def?.type)

  /*
  <a id={field.esIndex} href={specUrl} target='_blank'
       className={classNames(css.specificationCardHyperlink, !isArrayField && Classes.POPOVER_DISMISS)}
       onContextMenu={onContextMenu}
       data-selected={selected}
       onClick={ignoreHyperlinkClick}>
   */
  return (
    <div
      id={field.esIndex}
      className={classNames(css.specificationCardHyperlink, !isArrayField && Classes.POPOVER_DISMISS)}
      onContextMenu={onContextMenu}
      data-type='SidebarSpecification'
      data-selected={selected}
      onDoubleClick={onDoubleClickCard}
    >
      <Card
        elevation={0}
        className={css.specificationCard}
        data-id={catalog.idFor(data)}
        data-field={field.field}
        data-es-index={field.esIndex}
        data-type='SpecificationCard'
        data-in-popover={popover}
        data-as-text={renderSpecAsText}
        data-disable-actions={readonly}
        data-size={popover ? 'medium' : (catalogField?.editor?.size ?? 'medium')}
        onMouseEnter={() => specStore.mouseOver = !mouseOver}
        onMouseLeave={() => specStore.mouseOver = false}
        onClick={onClickCard}>
        {!renderSpecAsText && dataMap?.images !== null && <div className={css.imageDiv}>
          {image ? <MyImage objectFit={workspaces.wrapperByIndex[field.esIndex]?.objectFit}
                            imageUrl={image || ''}
                            imageId={firstImage?.exId}
                            className={css.image}/>
                 : <Placeholder className={css.placeholder}/>}
          {data?.attributes?.Active === false && <div className={classNames(css.diagonal)}/>}
		</div>}
        <CardContent className={css.content}>
          {fieldValues.map(({field: fieldName, fieldConfig: field, fieldConfig, label, style, value}, i) => {
            const color = associatedLibrary?.showStatusLabel && (dataMap?.active === field ? value ? 'var(--green)' : 'var(--red)' : '') || ''

            value = associatedLibrary?.showStatusLabel && (dataMap?.active === field ? value ? field?.def?.tileBooleanLabels?.trueLabel ?? strings.emptyLabel : field?.def?.tileBooleanLabels?.falseLabel ?? strings.emptyLabel : value) || value
            if (field?.def?.type === 'Ref' && field?.esIndex) {
              return <ListPreviewCardRefCatalogItem key={i} className={css.refItem} field={field} value={value}/>
            } else if (fieldConfig?.def?.type === 'RefList' && field?.esIndex) {
              return <ListPreviewCardRefListCatalogItems key={i} className={css.refListItem} field={field} value={value}/>
            } else {
              // return <Tooltip2 key={i} content={cardFieldStore || !_.isString(value) ? '' : value}>
              return <div key={i} style={{color: color}} className={css.fieldValue} data-style={style}>
                {React.isValidElement(value) ? value : _.isArray(value) ? value.join(', ') : _.isObject(value) ? JSON.stringify(value) : value}
              </div>
              // </Tooltip2>
            }
          })}
          {/*<Tooltip2 content={title} disableHoverListener><div data-attr-type={'title'} className={css.title}>{title}</div></Tooltip2>*/}
          {/*<Tooltip2 content={subtitle} disableHoverListener><div data-attr-type={'subtitle'} className={css.subtitle}>{subtitle}</div></Tooltip2>*/}
        </CardContent>
        {readonly === false &&
		 <CardActions className={css.actions}>
           {popover && specUrl &&
		    <Button className={css.link} minimal={false} icon='link'
		            title={`Open in '${catalog.labelForField(catalogField)}' Workspace`}
		            text=''
		            onClick={e => {
                      e.stopPropagation()
                      window.open(specUrl, '_blank')
                    }}/>}
           {allowUnassign && !isList && <PrimaryIconButton data-mouse-over={mouseOver} onClick={onDelete} className={css.close} icon={<Icon icon='delete'/>}/>}
           {isList && !popover && !disableListItem && <Fragment>
             {field?.refField &&
			  <Button className={classNames(css.link, css.listActions)} minimal={false} icon='tick'
			          text=''
			          title={`Promote`}
			          onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        promoteSpecItem()
                      }}/>
             }
			   <Button className={classNames(css.link, css.listActions)} minimal={false} icon='delete'
			           text=''
			           title={`Remove`}
			           onClick={e => {
                         e.preventDefault()
                         e.stopPropagation()
                         removeSpecItem()
                       }}/>
		   </Fragment>
           }
		 </CardActions>}
      </Card>
    </div>
  )
})

const SpecHeader = observer(function SpecHeader() {
  const {strings, availableSpecs, header, total, search, isList, isSearch, saveInputGroupRef, saveInputRef, toggleSearch, hasMore, _changeSearch, associatedLibrary, onDelete, field, rendererStore: {value, detailsStore, cardFieldStore}} = useSidebarSpecificationStore(),
    countHtml = _.isEmpty(availableSpecs) ? '' : total > availableSpecs.length ? `(${availableSpecs.length} / ${total})` : `(${total})`

  return (
    <div className={css.header}>
      <div className={classNames(css.text, {[css.hide]: isSearch})}>
        <div>{header}</div>
        <div>{countHtml}</div>
      </div>
      <PrimaryIconButton onClick={toggleSearch} icon={<Icon icon={'search'}/>}/>
      <InputGroup
        autoFocus
        ref={saveInputGroupRef}
        inputRef={saveInputRef}
        rightElement={<PrimaryIconButton className={css.closeSearcher} icon={<Icon icon={'cross'}/>} onClick={toggleSearch}/>}
        className={classNames(css.searcher, {[css.hide]: !isSearch, [css.input]: isSearch})}
        value={search}
        onChange={_changeSearch}
        placeholder={`Search...`}
      />
      {(cardFieldStore || detailsStore) && (field?.editable !== false) && !isList &&
	   <PrimaryIconButton disabled={value?.val == null}
	                      tooltip={strings.unassign}
	                      icon={<Icon icon='trash'/>} onClick={onDelete}/>}
      {associatedLibrary && <LibraryFiltersPopoverButton library={associatedLibrary} align={detailsStore?.sidebar?.align ?? 'inline'}/>}
    </div>)
})

export const SpecList = observer<{ selectedData?: any }>(function SpecList({selectedData}) {
  const {strings, availableSpecs, hasMore, header, initialLoad, _runQuery, loading, page, catalog: {idFor}} = useSidebarSpecificationStore(),
    selectedSet = useMemo(() => {
      if (_.isArray(selectedData)) {
        return new Set<string>(selectedData.filter(_.identity).map(s => idFor(s, false)))
      } else if (selectedData) {
        return new Set<string>([idFor(selectedData, false)])
      }

      return new Set<string>()
    }, [selectedData])

  // console.log({selectedSet, selectedData, hasMore, loading, page})
  return <Card className={css.specList}>
    <SpecHeader/>
    <Divider/>
    <CardContent className={css.listContent} data-spec-list={header}>
      <InfiniteScroll
        className={classNames(css.infiniteScroll, Classes.MENU)}
        pageStart={-1}
        useWindow={false}
        loadMore={page => _runQuery(page)}
        hasMore={hasMore}
        loader={<Spinner className={css.loader} key='spinner' size={20}/>}
      >
        {initialLoad || (loading && _.isEmpty(availableSpecs) && page === 0)
         ? <LoadingIndicator/>
         : !_.isEmpty(availableSpecs)
           ? availableSpecs.slice().map((d, i) => {
              const id = idFor(d)

              return (
                <SidebarSpecificationCard
                  selected={d != null && selectedSet.has(id)}
                  key={id}
                  popover
                  data={d._source}/>)
            })
           : !loading ? <div className={css.noResults}>{strings.noResults}</div> : <LoadingIndicator/>}
      </InfiniteScroll>
    </CardContent>
  </Card>
})

/**
 * A linked complex item like a color, material, shape, etc (or array there-of)
 * esIndex is guaranteed to be set on the passed in field
 */
export const SidebarSpecification = observer<{ children?: React.ReactNode, isPopoverOpen?: boolean, onOpenPopover?: Function, onClosePopover?: Function, field?: ICatalogFieldConfig, className?: any, data?: any, popoverPosition?: PopoverPosition, button?: any }, any>(function SidebarSpecification({onOpenPopover, onClosePopover, popoverPosition, className, button, isPopoverOpen, children, ...props}, ref) {
  const fieldRendererStore = useFieldRendererStore(),
    field = props.field ?? fieldRendererStore?.catalogField,
    data = props.data ?? fieldRendererStore?.value?.val,
    {field: {nested}} = fieldRendererStore,
    obsProps = useAsObservableSource({field, info: data}),
    store = useVipLocalStore((ws) => new SidebarSpecificationStore(ws, obsProps, fieldRendererStore)),
    {header, readonly, everOpened, onClickNestedCards, catalog: {settings: {showNestedFieldsOnTopLevelCard}, idFor}} = store,
    onOpening = useCallback(() => {
      store.popoverOpening()
      onOpenPopover && onOpenPopover()
    }, [onOpenPopover]),
    onClose = useCallback(() => {
      store.reset()
      onClosePopover && onClosePopover()
    }, [onClosePopover])

  useEffect(() => {
    store.registerEffects()
  }, [])

  DEBUG_CATALOG_ITEM_CARDS && console.log({fieldRendererStore, cardFieldStore: fieldRendererStore?.cardFieldStore})

  return <SidebarSpecificationStoreContext.Provider value={store}>
    {/** Editable spec cards have TWO popovers around them, one for click (this one), and another for hover in {@see SpecificationItem }**/
      <Popover2
        ref={ref}
        isOpen={isPopoverOpen}
        interactionKind={'click'}
        data-type='SidebarSpecification'
        className={classNames(css.specPopover, className)}
        minimal={false}
        disabled={readonly || fieldRendererStore?.cardFieldStore?.cardStore?.fieldsAreOnScreen === false}
        //usePortal={false} // If portal is on and we're in a material-ui modal then it steals focus from our input
        onClose={onClose}
        onOpening={onOpening}
        position={popoverPosition ?? 'auto'}
        lazy
        content={everOpened ? <SpecList selectedData={data}/> : <div/>}>

        {(data?.id || data?.exId)
         ? <Fragment>
           <SidebarSpecificationCard
             key={idFor(data)}
             data={data}
             allowUnassign={data != null}/>
           {nested && <div onClick={onClickNestedCards}><NestedXRefCards/></div>}
         </Fragment>
         : (children || button || <Fragment>
            {data?.length > 0
             ? <div className={css.listTypeSpec}>
               {_.map(data, (d, i) => <div className={css.listTypeSpecItem} key={idFor(d)}>
                 <SidebarSpecificationCard data={d} allowUnassign={d != null && !fieldRendererStore?.cardFieldStore}/>
                 {nested && <div onClick={onClickNestedCards}><NestedXRefCards nestedListItem={d}/></div>}
               </div>)}
             </div> : readonly ? <div className={css.listNoItems}>No Items</div> : null}
            {!readonly && <Button id={field.esIndex} className={css.button} disabled={readonly} data-button-type={'add-button'}>{`Add ${header}`}</Button>}
          </Fragment>)}
      </Popover2>}
  </SidebarSpecificationStoreContext.Provider>
}, {forwardRef: true})




// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/SidebarSpecification.tsx